import { createSlice } from "@reduxjs/toolkit";
import {
  fetchJudgeStages,
  fetchMissions,
  fetchProjectsWithFilter,
  submitReview,
} from "./thunks";

/**
 * @type {JudgeState}
 */
const initialState = {
  missions: [],
  isLoadingMissions: false,
  projectPaging: {
    page: 0,
    total: 0,
    page_size: 0,
    count: 0,
    results: [],
  },
  isLoadingProjects: false,
  currentMission: null,
  projectFilter: {
    search: "",
    order: null,
    status: "all",
    range: null,
  },
  stages: [],
  currentStage: "",
  reviewDraft: {
    phase: "0",
    score: void 0,
    review: "",
    memo: "",
  },
  editingProjectId: 0,
  isReviewDirty: false,
  isReviewSubmitted: false,
};

const slice = createSlice({
  name: "user/judge",
  initialState,
  reducers: {
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<JudgeMission>} action
     */
    setCurrentMission(state, action) {
      state.currentMission = action.payload;
      state.projectPaging = initialState.projectPaging;
    },
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<JudgeProjectQueryParams>} action
     */
    setProjectFilter(state, action) {
      state.projectFilter = action.payload;
    },
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<number>} action
     */
    setProjectPage(state, action) {
      // do nothing, handle in middleware
    },
    clearProjects(state) {
      state.projectPaging = initialState.projectPaging;
    },
    refetchProjects(state) {
      // do nothing, handle in middleware
    },
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<{id: number, draft: JudgeReviewDraft}>} action
     */
    initReviewDraft(state, action) {
      state.editingProjectId = action.payload.id;
      state.reviewDraft = action.payload.draft;
      state.isReviewDirty = false;
      state.isReviewSubmitted = false;
    },
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<JudgeReviewDraft>} action
     */
    setReviewDraft(state, action) {
      state.reviewDraft = action.payload;
      state.isReviewDirty = true;
    },
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<boolean>} action
     */
    setReviewDirty(state, action) {
      state.isReviewDirty = action.payload;
    },
    /**
     * @param state
     * @param {import('@reduxjs/toolkit').PayloadAction<boolean>} action
     */
    setReviewSubmitted(state, action) {
      state.isReviewSubmitted = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMissions.pending, (state) => {
      state.isLoadingMissions = true;
    });

    builder.addCase(fetchMissions.fulfilled, (state, action) => {
      state.isLoadingMissions = false;
      state.currentStage = action.payload.current_stage;
      state.missions = action.payload.results;
      state.missions.sort((a, b) => {
        const aStatus = a.review_status.status;
        const bStatus = b.review_status.status;
        if (aStatus === bStatus) {
          return (
            a.category.name.localeCompare(b.category.name) ||
            a.subcategory.name.localeCompare(b.subcategory.name)
          );
        }
        if (aStatus === "ongoing") {
          return -1;
        }
        if (bStatus === "ongoing") {
          return 1;
        }
        if (aStatus === "done") {
          return -1;
        }
        if (bStatus === "done") {
          return 1;
        }
        return 1;
      });
      if (state.currentMission !== null) {
        const currentMission = keyOfMission(state.currentMission);
        const newCurrentMission = state.missions.find(
          (m) => keyOfMission(m) === currentMission
        );
        if (newCurrentMission) {
          state.currentMission = newCurrentMission;
        }
      }
    });

    builder.addCase(fetchMissions.rejected, (state) => {
      // @TODO handle error
      state.isLoadingMissions = false;
      state.currentStage = "";
      state.missions = [];
    });

    builder.addCase(fetchProjectsWithFilter.pending, (state) => {
      state.isLoadingProjects = true;
    });

    builder.addCase(fetchProjectsWithFilter.fulfilled, (state, action) => {
      state.isLoadingProjects = false;
      state.projectPaging = action.payload;
    });

    builder.addCase(fetchProjectsWithFilter.rejected, (state) => {
      // @TODO handle error
      state.isLoadingProjects = false;
      state.projectPaging = [];
    });

    builder.addCase(fetchJudgeStages.pending, (state) => {
      // do nothing
    });

    builder.addCase(fetchJudgeStages.fulfilled, (state, action) => {
      state.stages = action.payload;
    });

    builder.addCase(fetchJudgeStages.rejected, (state) => {
      // @TODO handle error
      state.stages = [];
    });
  },
});

export default slice.reducer;

export const actions = {
  ...slice.actions,
  fetchMissions,
  fetchProjectsWithFilter,
  fetchJudgeStages,
  submitReview,
};

/**
 *
 * @param {JudgeMission} m
 * @returns {string}
 */
function keyOfMission(m) {
  return [m.year, m.category.shortcut, m.subcategory.shortcut].join(":");
}
